import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import RHFSelect from "@/components/form/RHFSelect";
import useLiveResource from "@/hooks/useLiveResource";
import { Input } from "@/components/ui/Input";
import TimezoneSelect from "@/components/ui/TimezoneSelect";
import ShiftTimingService from "@/services/shiftTimingService";
import { flattenErrors } from "@/utils/formErrorUtils";
import { to24HourDisplay } from "@/utils/shiftTime";

const CUSTOM_OPTION_VALUE = "__custom__";
const NONE_OPTION_VALUE = "__none__";

/**
 * Shift selection for Create/Edit/Clone job (all three render JobForm).
 * Lists the tenant's predefined shifts (Organization Settings) plus "No
 * shift" and "Custom Shift". Choosing a predefined shift only needs its id —
 * start/end/timezone are resolved and snapshotted server-side. Choosing
 * Custom reveals mandatory start/end/timezone fields, validated by the
 * shared rule in ../../../pages/admin/job/shift.schema.js (also used by the
 * Organization Settings predefined-shift modal).
 *
 * The list is fetched unfiltered rather than `is_active: true`, for one
 * case: a job already holding a shift the admin has since disabled. Filtered
 * out, its id matched no option, the picker fell back to the placeholder and
 * read as if no shift were set, and every save 422'd on the disabled shift.
 * So the disabled shift the job already holds is listed, labelled as
 * disabled; every other disabled shift stays out of the list, which is what
 * "hides it from new job selections" means.
 */
const ShiftSelector = ({ formMethods, disabled = false, legacyShiftTiming }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const { watch, setValue, formState } = formMethods;
  const flattened = flattenErrors(formState.errors);

  const shiftType = watch("shiftType");
  const predefinedShiftId = watch("predefinedShiftId");
  const shiftStartTime = watch("shiftStartTime");
  const shiftEndTime = watch("shiftEndTime");
  const shiftTimezone = watch("shiftTimezone");

  // The shift the job arrived with. Captured once, so switching the picker
  // to another shift does not keep the disabled one on the list.
  const attachedShiftId = useRef(predefinedShiftId || "");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await ShiftTimingService.list();
      setShifts(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      console.error("Failed to load predefined shifts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Organization Settings publishes `shift_timing.changed` to the tenant on
  // every add, rename and disable, and nothing listened — so a job form left
  // open kept offering a shift the admin had just retired, and never saw a new
  // one, until the page was reloaded.
  useLiveResource({
    keys: "shift_timing",
    onChange: load,
  });

  const options = useMemo(() => {
    const selectable = shifts.filter((s) => s.isActive || s.id === attachedShiftId.current);
    return [
      { value: NONE_OPTION_VALUE, label: "No shift" },
      ...selectable.map((s) => ({
        value: s.id,
        label: `${s.name} (${to24HourDisplay(s.startTime)} - ${to24HourDisplay(s.endTime)}, ${s.timezone})${s.isActive ? "" : " — disabled"}`,
      })),
      { value: CUSTOM_OPTION_VALUE, label: "Custom Shift" },
    ];
  }, [shifts]);

  const selectionValue = shiftType === "custom" ? CUSTOM_OPTION_VALUE : shiftType === "predefined" ? predefinedShiftId : shiftType === "none" ? NONE_OPTION_VALUE : "";

  const clearCustomFields = () => {
    setValue("shiftStartTime", "", { shouldDirty: true });
    setValue("shiftEndTime", "", { shouldDirty: true });
    setValue("shiftTimezone", "", { shouldDirty: true });
  };

  const handleSelectionChange = (value) => {
    if (value === CUSTOM_OPTION_VALUE) {
      setValue("shiftType", "custom", { shouldValidate: true, shouldDirty: true });
      setValue("predefinedShiftId", "", { shouldDirty: true });
      return;
    }
    if (value === NONE_OPTION_VALUE) {
      setValue("shiftType", "none", { shouldValidate: true, shouldDirty: true });
      setValue("predefinedShiftId", "", { shouldDirty: true });
      clearCustomFields();
      return;
    }
    setValue("shiftType", "predefined", { shouldValidate: true, shouldDirty: true });
    setValue("predefinedShiftId", value, { shouldValidate: true, shouldDirty: true });
    clearCustomFields();
  };

  const isCustom = shiftType === "custom";

  return (
    <div className="flex flex-col gap-3" data-field="shiftSelector">
      <div>
        <RHFSelect
          name="predefinedShiftId"
          label="Shift Timing"
          options={options}
          searchable
          searchPlaceholder="Search shifts..."
          placeholder={loading ? "Loading shifts..." : "Select a shift or choose Custom"}
          disabled={disabled || loading}
          value={selectionValue || ""}
          onChange={handleSelectionChange}
        />
        {!shiftType && legacyShiftTiming ? (
          <p className="text-xs text-text-muted mt-1">Currently set to: {legacyShiftTiming}. Select a shift above to replace it.</p>
        ) : null}
      </div>

      {isCustom && (
        <div className="flex flex-col sm:flex-row gap-2" data-field="shiftStartTime">
          <div className="flex-1">
            <label htmlFor="shiftStartTime" className="block text-xs mb-1 text-text-muted">
              Start Time <span className="text-red-500 font-bold">*</span>
            </label>
            <Input
              id="shiftStartTime"
              type="time"
              value={shiftStartTime}
              onChange={(e) => setValue("shiftStartTime", e.target.value, { shouldValidate: true, shouldDirty: true })}
              disabled={disabled}
              className={flattened.shiftStartTime ? "border-red-500" : ""}
            />
            {flattened.shiftStartTime && <p className="text-red-400 text-xs mt-1">{flattened.shiftStartTime.message}</p>}
          </div>
          <div className="flex-1" data-field="shiftEndTime">
            <label htmlFor="shiftEndTime" className="block text-xs mb-1 text-text-muted">
              End Time <span className="text-red-500 font-bold">*</span>
            </label>
            <Input
              id="shiftEndTime"
              type="time"
              value={shiftEndTime}
              onChange={(e) => setValue("shiftEndTime", e.target.value, { shouldValidate: true, shouldDirty: true })}
              disabled={disabled}
              className={flattened.shiftEndTime ? "border-red-500" : ""}
            />
            {flattened.shiftEndTime && <p className="text-red-400 text-xs mt-1">{flattened.shiftEndTime.message}</p>}
          </div>
          <div className="flex-1" data-field="shiftTimezone">
            {/* TimezoneSelect renders `error` itself — a sibling <p> here
                printed the same sentence twice. */}
            <TimezoneSelect
              value={shiftTimezone}
              onChange={(val) => setValue("shiftTimezone", val, { shouldValidate: true, shouldDirty: true })}
              label={
                <span>
                  Time Zone <span className="text-red-500 font-bold">*</span>
                </span>
              }
              disabled={disabled}
              error={flattened.shiftTimezone?.message}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftSelector;
